import { nanoid } from "nanoid"
import type { CompanyProfile } from "@/utils/repositories/seo"
import { jobShopTag, tenantTag } from "@/utils/cacheTags"
import { ORG_LOCATION_LIMIT, extractCities } from "@/utils/organizationSchema"
import Logger from "@/utils/Logger"

// Offers scanned to collect unique job cities (Typesense max hits per page).
const OFFER_SCAN_LIMIT = 250

export interface OrganizationData {
  profile: CompanyProfile | null
  cities: string[]
}

/**
 * Fetches the data needed for the site-wide Organization schema: the KPI company
 * profile (name, employees, website) and unique job cities from Typesense. Both
 * are cached in the shared data cache so emitting the schema on every page (via
 * the default layout) costs at most one KPI + one Typesense request per cache
 * window, not per page render.
 *
 * Follows the codebase async-data composable pattern: all instance-bound hooks
 * are resolved before the single `useAsyncData` await, and no getCurrentInstance
 * hook is called afterwards. The Typesense key is the layout's own
 * useTypesenseApiKey result, passed in as a promise, so the render makes one
 * /search/api-key request for it rather than two. Each fetch is fault-isolated
 * so a missing profile or Typesense outage still yields usable (if sparser) data. SSR-only: the data
 * only feeds server-rendered JSON-LD, so it is skipped on client navigations
 * (the hydration payload is reused). (JET-1819)
 */
export async function useOrganizationData(typesenseApiKey: Promise<string | null | undefined>) {
  const { $api } = useNuxtApp()
  const { tenantId, jobShopId } = useHeaderStore()
  const { dataCacheTtl, dataCacheStaleTtl, public: { typesenseUrl } } = useRuntimeConfig()
  const requestId = nanoid()

  const fetchCompanyProfile = async (): Promise<CompanyProfile | null> => {
    // Cache the profile in the shared data cache (stale-if-error on upstream
    // outage), tagged by tenant so the tenant invalidation path purges it. The
    // data-cache prefix already scopes every entry by tenant+jobshop, so the
    // item key needs no id of its own.
    const cacheKey = "seoCompanyProfile"
    try {
      return await useDataCacheWithStale(cacheKey, () => $api.seo.getCompanyProfile(tenantId), {
        maxAge: dataCacheTtl,
        staleIfError: dataCacheStaleTtl,
        requestId,
        tags: [tenantTag(tenantId)],
      })
    }
    catch (error) {
      Logger(requestId).error(`Failed to fetch company profile for Organization schema (tenant: ${tenantId}):`, error)
      return null
    }
  }

  const fetchCities = async (): Promise<string[]> => {
    if (!typesenseUrl)
      return []

    // Cache the cities (tagged by jobshop so offer-change invalidation refreshes
    // them) so the schema stays cheap on every page. The data-cache prefix
    // already scopes every entry by tenant+jobshop, so the item key needs no id.
    const cacheKey = "organizationCities"
    try {
      return await useDataCacheWithStale(cacheKey, async () => {
        const apiKey = await typesenseApiKey
        // Throw rather than caching an empty result when the key is unavailable,
        // so a transient key failure serves stale (or retries) instead of
        // freezing an empty city list for the whole cache window.
        if (!apiKey)
          throw new Error("Typesense API key unavailable")

        // Server-only, so no 401 retry (see withTypesenseKeyRetry).
        const response = await $api.typesense.getOffers(tenantId, jobShopId, apiKey, typesenseUrl, {
          includeFields: "location",
          perPage: OFFER_SCAN_LIMIT,
        })
        return extractCities(response, ORG_LOCATION_LIMIT)
      }, {
        maxAge: dataCacheTtl,
        staleIfError: dataCacheStaleTtl,
        requestId,
        tags: [jobShopTag(jobShopId)],
      })
    }
    catch (error) {
      Logger(requestId).error(`Failed to fetch job locations for Organization schema (tenant: ${tenantId}, jobshop: ${jobShopId}):`, error)
      return []
    }
  }

  const { data } = await useAsyncData<OrganizationData>(`organizationData-${jobShopId}`, async () => {
    // The data only feeds server-rendered JSON-LD; skip the KPI + Typesense work
    // on client-side navigations (crawlers read the schema from the SSR HTML).
    if (import.meta.client)
      return { profile: null, cities: [] }

    const [profile, cities] = await Promise.all([fetchCompanyProfile(), fetchCities()])
    return { profile, cities }
  })

  return { organizationData: data }
}
